import * as monaco from "monaco-editor";
import type {
  InlineCompletionItem,
  InlineCompletionItemProvider,
  InlineCompletionList,
  TextDocument,
  CancellationToken,
  InlineCompletionContext,
} from "vscode";
import * as vscode from "vscode";
import type { IEditorService } from "vscode/services";
import type {
  IDiffEditor,
  IEditor,
} from "vscode/vscode/vs/editor/common/editorCommon";

import { executeVSCodeCommand } from "utils/vscode";

import type { EditorHistoryService } from "../editor-history";

import type {
  ICompletionRequestOpts,
  ICompletionItem as ICodeiumCompletion,
} from "./codeium-core";
import { getCompletions } from "./codeium-core";
import type { TextAndOffsets } from "./compute-text";
import { OPT_OUT_LENGTH, computeTextAndOffsets } from "./compute-text";
import { numUtf8BytesToNumCodeUnits } from "./utf";

function createInlineCompletionItem(
  completionItem: ICodeiumCompletion,
  document: TextDocument,
  additionalUtf8ByteOffset: number,
  editorService: IEditorService,
): InlineCompletionItem | undefined {
  if (!completionItem.completion || !completionItem.range) {
    return undefined;
  }

  // Create and return inlineCompletionItem.
  const text = document.getText();
  const startPosition = document.positionAt(
    numUtf8BytesToNumCodeUnits(
      text,
      completionItem.range.startOffset - additionalUtf8ByteOffset,
    ),
  );
  const endPosition = document.positionAt(
    numUtf8BytesToNumCodeUnits(
      text,
      completionItem.range.endOffset - additionalUtf8ByteOffset,
    ),
  );
  const range = new vscode.Range(startPosition, endPosition);

  let completionText = completionItem.completion.text;
  let callback: () => void = () => {};

  if (completionItem.suffix && completionItem.suffix.text.length > 0) {
    let activeEditor: IEditor | undefined;
    if (editorService.activeTextEditorControl) {
      if (
        typeof (editorService.activeTextEditorControl as IDiffEditor)
          .getModifiedEditor !== "undefined"
      ) {
        activeEditor = (
          editorService.activeTextEditorControl as IDiffEditor
        ).getModifiedEditor();
      } else {
        activeEditor = editorService.activeTextEditorControl;
      }
    }
    const activeEditorModel = activeEditor?.getModel() as
      | monaco.editor.ITextModel
      | undefined;
    if (activeEditorModel?.uri.toString() === document.uri.toString()) {
      // Add suffix to the completion text.
      completionText += completionItem.suffix.text;
      // Create callback to move cursor after accept.
      // Note that this is a hack to get around Monaco's API limitations.
      // There's no need to convert to code units since we only use simple characters.
      const deltaCursorOffset = Number(completionItem.suffix.deltaCursorOffset);
      callback = () => {
        const selection = activeEditor?.getSelection();
        if (selection == null) {
          // eslint-disable-next-line
          console.warn("Unexpected, no selection");
          return;
        }
        const monacoPos = selection.getPosition();
        const position = new vscode.Position(
          monacoPos.lineNumber,
          monacoPos.column,
        );
        const newPosition = document.positionAt(
          document.offsetAt(position) + deltaCursorOffset,
        );
        activeEditor?.setSelection(
          new monaco.Range(
            newPosition.line,
            newPosition.character,
            newPosition.line,
            newPosition.character,
          ),
        );
        executeVSCodeCommand("editor.action.inlineSuggest.trigger");
      };
    }
  }

  const inlineCompletionItem: InlineCompletionItem = {
    insertText: completionText,
    range,
    command: {
      title: "Accept Completion",
      command: "codeium.acceptCompletion",
      arguments: [completionItem.completion.completionId, callback],
    },
  };
  return inlineCompletionItem;
}

export class CodeiumCompletionProvider implements InlineCompletionItemProvider {
  private openModels = new Map<string, TextDocument>();

  constructor(
    private username: string,
    private editorService: IEditorService,
    private historyService: EditorHistoryService,
    private toggleLoadingState: (loading: boolean) => void,
  ) {
    vscode.workspace.onDidOpenTextDocument((t) => {
      this.openModels.set(t.uri.toString(), t);
    });

    vscode.workspace.onDidCloseTextDocument((t) => {
      this.openModels.delete(t.uri.toString());
    });
  }

  private computeTextAndOffsets(
    document: TextDocument,
    position: vscode.Position,
  ): TextAndOffsets {
    return computeTextAndOffsets({
      history: this.historyService.getHistory(),
      currentDocument: document,
      utf16CodeUnitOffset: document.offsetAt(position),
      getText: (document: TextDocument) => document.getText(),
      getOpenModel: (uri: string) => this.openModels.get(uri),
    });
  }

  async provideInlineCompletionItems(
    document: TextDocument,
    position: vscode.Position,
    _context: InlineCompletionContext,
    token: CancellationToken,
  ): Promise<InlineCompletionItem[] | InlineCompletionList> {
    // Abort logic
    const abortController = new AbortController();
    token.onCancellationRequested(() => {
      abortController.abort();
    });
    if (token.isCancellationRequested) {
      return [];
    }

    // Bail out on really large text documents?
    const docLen = document.getText().length;
    if (docLen > OPT_OUT_LENGTH) {
      // eslint-disable-next-line
      console.warn(
        "Bailing out of autocomplete for large document:",
        document.uri,
        docLen,
      );
      return [];
    }

    const { text, utf8ByteOffset, additionalUtf8ByteOffset } =
      this.computeTextAndOffsets(document, position);

    const req: ICompletionRequestOpts = {
      uid: this.username,
      document: {
        text,
        language: document.languageId,
        cursorOffset: utf8ByteOffset + additionalUtf8ByteOffset,
        lineEnding: document.eol === 1 ? "\n" : "\r\n",
      },
      // TODO: Get this from editor
      editorOptions: {
        tabSize: 2,
        insertSpaces: true,
      },
    };

    this.toggleLoadingState(true);

    const result = await getCompletions(req, abortController.signal).finally(
      () => {
        this.toggleLoadingState(false);
      },
    );

    const completionItems: InlineCompletionItem[] = result
      .map((entry) => {
        return createInlineCompletionItem(
          entry,
          document,
          additionalUtf8ByteOffset,
          this.editorService,
        ) as InlineCompletionItem;
      })
      .filter(Boolean);

    return completionItems;
  }
}
